import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import styles from "./Header.module.css";
import validateToken from "../utils/validateToken";
import { USER_TYPES } from "../types/userType";
import { BarLoader } from "react-spinners";
import useIsRental from "../hooks/useIsRental";
import useIsAdmin from "../hooks/useIsAdmin";
import useIsSuperAdmin from "../hooks/useIsSuperAdmin";
import useIsInspection from "../hooks/useIsInspection";
import useIsOperate from "../hooks/useIsOperate";
import useIsFreesbe from "../hooks/useIsFreesbe";
import useIsMobile from "../hooks/useIsMobile";
import ChatHeader from "./ChatHeader";
import Menu3Dots from "./icons/Menu3Dots";
import TableIcon from "./icons/TableIcon";
import LogOutIcon from "./icons/LogOutIcon";
import NavLinkWithIcon from "./NavLinkWithIcon";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "../design-system/tokens";

const menuPanelStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.background,
  boxShadow: tokens.shadows.md,
  borderRadius: tokens.borderRadius.xl,
  ["--header-menu-item-hover-bg" as string]: tokens.buttonColors.secondaryLightHover,
};

const menuItemStyle: React.CSSProperties = {
  ...tokens.typography.menuItem,
  color: tokens.colors.text.primary,
  borderRadius: tokens.borderRadius.md,
};

/** CSS vars for responsive nav font size (see Header.module.css). */
const navShellStyle: React.CSSProperties = {
  ["--header-nav-font-size" as string]: tokens.typography.h4.fontSize,
  ["--header-nav-font-size-sm" as string]: tokens.typography.h6.fontSize,
};

const navLinkBaseStyle: React.CSSProperties = {
  fontFamily: tokens.typography.body.fontFamily,
  fontWeight: tokens.typography.body.fontWeight,
  lineHeight: tokens.typography.body.lineHeight,
  fontSize: tokens.typography.body.fontSize,
  color: tokens.colors.text.primary,
  textDecoration: "none",
};

const navLinkActiveStyle: React.CSSProperties = {
  ...navLinkBaseStyle,
  fontWeight: 800,
  color: tokens.colors.text.secondary,
};

const separatorStyle: React.CSSProperties = {
  fontFamily: tokens.typography.h4.fontFamily,
  color: tokens.colors.text.tertiary,
};

const Header: React.FC = () => {
  const { logout, isAuthenticated, userType, setUserType, loading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isRental = useIsRental();
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const [isChatPage, setIsChatPage] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isInspection = useIsInspection();
  const isOperate = useIsOperate();
  const isFreesbe = useIsFreesbe();

  const handleToggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    closeMenu();
    logout();
    navigate("/login");
  };

  const isMobile = useIsMobile();

  useEffect(() => {
    // is /chat page
    if (window.location.pathname === "/chat") {
      setIsChatPage(true);
    } else {
      setIsChatPage(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    const checkToken = async (token: string) => {
      const data = await validateToken(token);
      if (data.message === "Token has expired or is invalid") {
        logout();
        navigate("/login");
        return;
      }
      if (data.userType !== userType) {
        // if not include ADMIN OR SUPER_ADMIN, logout
        if (
          !data.userType.includes(USER_TYPES.ADMIN) &&
          !data.userType.includes(USER_TYPES.SUPER_ADMIN) &&
          !data.userType.includes(USER_TYPES.RENTAL) &&
          !data.userType.includes(USER_TYPES.INSPECTION) &&
          !data.userType.includes(USER_TYPES.OPERATE) &&
          !data.userType.includes(USER_TYPES.FREESBE)
        ) {
          logout();
          navigate("/login");
          return;
        }

        setUserType(data.userType);
        localStorage.setItem("userType", JSON.stringify(data.userType));
      }
    };
    if (token) {
      checkToken(token);
    }
  }, [logout, navigate, setUserType, userType]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  let menu = <></>;
  if (isRental && !isAdmin && !isSuperAdmin) {
    menu = (
      <>
        <nav className={styles.nav} style={navShellStyle}>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/cars"
          >
            מספרי רכבים
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/test-drive"
          >
            נסיעות מבחן
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/chat"
          >
            צ'אט
          </NavLink>
        </nav>
        <div className={styles.dropdownMenu} ref={menuRef}>
          <div
            onClick={handleToggleMenu}
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
          >
            <Menu3Dots />
          </div>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className={styles.menuContent}
                style={menuPanelStyle}
                initial={{ opacity: 1, y: -6 }}
                animate={{ opacity: 1, y: -6 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0, ease: "easeOut" }}
              >
                <NavLink
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/car-inspection-table"
                  onClick={closeMenu}
                >
                  טבלת ביקורות רכב
                </NavLink>
                {isAuthenticated && (
                  <NavLinkWithIcon
                    icon={<LogOutIcon />}
                    className={styles.menuItem}
                    onClick={handleLogout}
                    style={menuItemStyle}
                  >
                    התנתק
                  </NavLinkWithIcon>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  } else if ((isOperate || isFreesbe) && !isAdmin && !isSuperAdmin) {
    menu = (
      <>
        <nav className={styles.nav} style={navShellStyle}>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/accident-report-table"
          >
            טבלת דוחות תאונה
          </NavLink>
        </nav>
        <div className={styles.dropdownMenu} ref={menuRef}>
          <div
            onClick={handleToggleMenu}
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
          >
            <Menu3Dots />
          </div>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className={styles.menuContent}
                style={menuPanelStyle}
                initial={{ opacity: 1, y: -6 }}
                animate={{ opacity: 1, y: -6 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0, ease: "easeOut" }}
              >
                {isAuthenticated && (
                  <NavLinkWithIcon
                    icon={<LogOutIcon />}
                    className={styles.menuItem}
                    onClick={handleLogout}
                    style={menuItemStyle}
                  >
                    התנתק
                  </NavLinkWithIcon>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  } else if (isInspection && !isAdmin && !isSuperAdmin) {
    menu = (
      <>
        <nav className={styles.nav} style={navShellStyle}>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/car-inspection-table"
          >
            טבלת ביקורות רכב
          </NavLink>
          {/* <span className={styles.separator}>|</span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            to="/accident-report-table"
          >
            טבלת דוחות תאונה
          </NavLink> */}
        </nav>
        <div className={styles.dropdownMenu} ref={menuRef}>
          <div
            onClick={handleToggleMenu}
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
          >
            <Menu3Dots />
          </div>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className={styles.menuContent}
                style={menuPanelStyle}
                initial={{ opacity: 1, y: -6 }}
                animate={{ opacity: 1, y: -6 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0, ease: "easeOut" }}
              >
                {isAuthenticated && (
                  <NavLinkWithIcon
                    icon={<LogOutIcon />}
                    className={styles.menuItem}
                    onClick={handleLogout}
                    style={menuItemStyle}
                  >
                    התנתק
                  </NavLinkWithIcon>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  } else if (isAdmin || isSuperAdmin) {
    menu = (
      <>
        <nav className={styles.nav} style={navShellStyle}>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/users"
          >
            משתמשים
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/codes"
          >
            מספרי תדלוק
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/cars"
          >
            מספרי רכבים
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/test-drive"
          >
            נסיעות מבחן
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/chat"
          >
            צ'אט
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/app-stats"
          >
            סטטיסטיקות
          </NavLink>
          <span className={styles.separator} style={separatorStyle}>
            |
          </span>
          <NavLink
            className={({ isActive }) =>
              isActive ? styles.active : styles.navLink
            }
            style={({ isActive }) => (isActive ? navLinkActiveStyle : navLinkBaseStyle)}
            to="/login-logs"
          >
            יומן כניסות
          </NavLink>

        </nav>
        <div className={styles.dropdownMenu} ref={menuRef}>
          <div
            onClick={handleToggleMenu}
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
          >
            <Menu3Dots />
          </div>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className={styles.menuContent}
                style={menuPanelStyle}
                initial={{ opacity: 1, y: -6 }}
                animate={{ opacity: 1, y: -6 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0, ease: "easeOut" }}
              >
                <span className={styles.menuItemTitle}>טבלאות טפסים</span>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/car-inspection-table"
                  onClick={closeMenu}
                >
                  טבלת ביקורות רכב
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/accident-report-table"
                  onClick={closeMenu}
                >
                  טבלת דוחות תאונה
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/big-car-submissions-table"
                  onClick={closeMenu}
                >
                  התחייבות בגין קבלת רכב צמוד גדול
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/maagar-submissions-table"
                  onClick={closeMenu}
                >
                  אישור קבלת רכב מאגר
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/electric-cars-submissions-table"
                  onClick={closeMenu}
                >
                  התחייבות לטעינה ושימוש ברכב חשמלי - צמודים
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/driver-statement-submissions-table"
                  onClick={closeMenu}
                >
                  הצהרת נוהג/ת ברכבי רפאל
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/new-driver-statement-submissions-table"
                  onClick={closeMenu}
                >
                  טפסים לקבלה ולנהיגה ברכב צמוד
                </NavLinkWithIcon>
                <NavLinkWithIcon
                  icon={<TableIcon />}
                  className={styles.menuItem}
                  style={menuItemStyle}
                  to="/family-form-submissions-table"
                  onClick={closeMenu}
                >
                  הצהרת נוהגים בני משפחה
                </NavLinkWithIcon>

                {/* divider */}
                <div className={styles.menuItemDivider}></div>
                {isAuthenticated && (
                  <NavLinkWithIcon
                    icon={<LogOutIcon />}
                    className={styles.menuItem}
                    onClick={handleLogout}
                    style={menuItemStyle}
                    size={15}
                  >
                    התנתק
                  </NavLinkWithIcon>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  }

  let header = (
    <>
      <header className={styles.header}>{menu}</header>
      {loading && (
        <div className={styles.loading}>
          <BarLoader width="100%" color="#28a745" />
        </div>
      )}
      <div className={styles.headerSpacer}></div>
    </>
  );

  if (isChatPage && isMobile) {
    header = <ChatHeader />;
  }
  return header;
};

export default Header;
